import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { PencilIcon } from 'lucide-react'
import { useState } from 'react'
import type { DocumentSequenceResource } from './api'
import { useCompanySequences, usePreviewSequence } from './api'
import { CompanySequenceDialog } from './company-sequence-dialog'

export function CompanySequences({ groupId, companyId }: { groupId: string; companyId: string }) {
  const { t } = useTranslation()
  const sequences = useCompanySequences(groupId, companyId)
  const messages = useAppErrorMessages()
  const [editing, setEditing] = useState<DocumentSequenceResource | null>(null)
  if (sequences.isLoading) return <ListSkeleton columns={[1.5, 1, 1, 0.75, 1, 1, 0.5]} rows={8} />
  if (sequences.isError)
    return <AppError error={sequences.error} messages={messages} retry={sequences.refetch} />

  const list = sequences.data ?? []
  return (
    <section data-testid="company-sequences" aria-labelledby="company-sequences-heading">
      <div className="mb-3">
        <h2 id="company-sequences-heading" className="text-h3">
          {t('companies.sequences.title')}
        </h2>
        <p className="text-small text-muted-foreground">{t('companies.sequences.subtitle')}</p>
      </div>
      {list.length === 0 ? (
        <EmptyState
          title={t('companies.sequences.empty')}
          description={t('companies.sequences.emptyDescription')}
        />
      ) : (
        <div className="overflow-x-auto">
          <Table
            className="min-w-[860px] border border-border"
            data-testid="company-sequences-table"
          >
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead>{t('companies.sequences.type')}</TableHead>
                <TableHead>{t('companies.sequences.prefix')}</TableHead>
                <TableHead className="text-end">{t('companies.sequences.next')}</TableHead>
                <TableHead className="text-end">{t('companies.sequences.padding')}</TableHead>
                <TableHead>{t('companies.sequences.resetLabel')}</TableHead>
                <TableHead>{t('companies.sequences.preview')}</TableHead>
                <TableHead className="text-end">{t('companies.sequences.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((sequence) => (
                <SequenceRow
                  key={sequence.id}
                  sequence={sequence}
                  groupId={groupId}
                  companyId={companyId}
                  onEdit={setEditing}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {editing ? (
        <CompanySequenceDialog
          key={editing.id}
          sequence={editing}
          groupId={groupId}
          companyId={companyId}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      ) : null}
    </section>
  )
}

function SequenceRow({
  sequence,
  groupId,
  companyId,
  onEdit,
}: {
  sequence: DocumentSequenceResource
  groupId: string
  companyId: string
  onEdit: (sequence: DocumentSequenceResource) => void
}) {
  const { t } = useTranslation()
  const [previewOpen, setPreviewOpen] = useState(false)
  const preview = usePreviewSequence(
    groupId,
    companyId,
    previewOpen ? sequence.document_type : null,
  )
  return (
    <TableRow data-testid={`company-sequence-row-${sequence.id}`}>
      <TableCell className="text-identifier font-mono">{sequence.document_type}</TableCell>
      <TableCell className="text-identifier font-mono">{sequence.prefix}</TableCell>
      <TableCell className="text-end font-mono tabular-nums">{sequence.next_number}</TableCell>
      <TableCell className="text-end font-mono tabular-nums">{sequence.padding}</TableCell>
      <TableCell>{resetLabel(sequence.reset_frequency, t)}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setPreviewOpen((open) => !open)}
          aria-expanded={previewOpen}
          data-testid={`company-sequence-preview-${sequence.id}`}
        >
          {t('companies.sequences.preview')}
        </Button>
        {previewOpen ? (
          <span className="ms-2 text-identifier font-mono" aria-live="polite">
            {preview.isLoading
              ? t('companies.common.loading')
              : preview.isError
                ? t('companies.sequences.previewFailed')
                : (preview.data?.preview ?? t('companies.common.empty'))}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onEdit(sequence)}
          aria-label={t('companies.sequences.edit')}
          data-testid={`company-sequence-edit-${sequence.id}`}
        >
          <PencilIcon className="size-3.5" aria-hidden="true" />
        </Button>
      </TableCell>
    </TableRow>
  )
}

export function resetLabel(value: string, t: (key: string) => string): string {
  if (value === 'never' || value === 'yearly' || value === 'monthly')
    return t(`companies.sequences.reset.${value}`)
  return value
}
